import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";

interface BattleExitDialogProps {
  onConfirm: () => Promise<void>;
  onOpenChange?: (open: boolean) => void;
}

export function BattleExitDialog({ onConfirm, onOpenChange }: BattleExitDialogProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [leaving, setLeaving] = useState(false);
  const [failed, setFailed] = useState(false);

  const changeOpen = (nextOpen: boolean) => {
    setOpen(nextOpen);
    onOpenChange?.(nextOpen);
  };

  useEffect(() => {
    if (!open) {
      return;
    }

    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape" && !leaving) {
        setOpen(false);
        onOpenChange?.(false);
      }
    };
    window.addEventListener("keydown", closeOnEscape);
    return () => window.removeEventListener("keydown", closeOnEscape);
  }, [leaving, onOpenChange, open]);

  const confirm = async () => {
    if (leaving) {
      return;
    }

    setLeaving(true);
    setFailed(false);
    try {
      await onConfirm();
      changeOpen(false);
    } catch {
      setFailed(true);
    } finally {
      setLeaving(false);
    }
  };

  return (
    <>
      <button
        className="battle-exit-button"
        type="button"
        aria-label={t("battleExit")}
        onClick={() => {
          setFailed(false);
          changeOpen(true);
        }}
      >
        <ExitIcon />
      </button>
      {open ? createPortal(
        <div className="battle-confirm" role="presentation">
          <section
            className="battle-confirm__card"
            role="dialog"
            aria-modal="true"
            aria-labelledby="battle-exit-title"
            aria-describedby="battle-exit-description"
          >
            <div className="battle-confirm__icon" aria-hidden="true">
              <ExitIcon />
            </div>
            <h2 id="battle-exit-title">{t("battleExitTitle")}</h2>
            <p id="battle-exit-description">{t("battleExitWarning")}</p>
            {failed ? <p className="battle-confirm__error" role="alert">{t("battleExitError")}</p> : null}
            <div className="battle-confirm__actions">
              <button autoFocus type="button" disabled={leaving} onClick={() => changeOpen(false)}>
                {t("battleStay")}
              </button>
              <button
                className="battle-confirm__danger"
                type="button"
                disabled={leaving}
                onClick={() => void confirm()}
              >
                {leaving ? t("battleLeaving") : t("battleExitConfirm")}
              </button>
            </div>
          </section>
        </div>,
        document.body
      ) : null}
    </>
  );
}

function ExitIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M10 4H5.8A1.8 1.8 0 0 0 4 5.8v12.4A1.8 1.8 0 0 0 5.8 20H10" />
      <path d="M14 8l4 4-4 4M8 12h10" />
    </svg>
  );
}
